import { useState } from 'react';
import { 
  Wifi, MapPin, AlertTriangle, CheckCircle,
  Car, Trash2, Wind,
  ChevronDown, ChevronUp, Share2, QrCode, Download, Star
} from 'lucide-react';

interface GuidebookSection {
  id: string;
  title: string;
  icon: any;
  content: string;
  items?: { label: string; value: string }[];
}

export default function DigitalGuidebook() {
  const [expandedSections, setExpandedSections] = useState<string[]>(['wifi']);
  const [showQR, setShowQR] = useState(false);

  const toggleSection = (id: string) => {
    setExpandedSections(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const sections: any[] = [];

  const recommendations: any[] = [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center py-4">
        <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Guía del Huésped</h2>
        <p className="text-slate-400">Tu propiedad</p>
        <div className="flex justify-center gap-3 mt-4">
          <button 
            onClick={() => setShowQR(true)}
            className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <QrCode className="w-4 h-4" />
            Ver QR
          </button>
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Compartir
          </button>
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Download className="w-4 h-4" />
            Descargar
          </button>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-5 rounded-xl border border-blue-500/30">
        <h3 className="font-semibold text-white mb-2">¡Bienvenido!</h3>
        <p className="text-slate-300 text-sm">
          Gracias por elegir nuestro apartamento. Esta guía contiene toda la información 
          que necesitas para una estadía cómoda. Si tienes alguna pregunta, no dudes en contactarnos.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {sections.length === 0 && <div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
          <p className="font-semibold text-[var(--v-text-primary)]">Sin datos todavía</p>
          <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Esta sección se llenará con información real cuando registres propiedades y actividad.</p>
          </div>}

        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          
          return (
            <div key={section.id} className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-white font-semibold">{section.title}</h4>
                    <p className="text-slate-400 text-sm">{section.content}</p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {isExpanded && section.items && (
                <div className="px-4 pb-4">
                  <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                    {section.items.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">{item.label}</span>
                        <span className="text-white font-medium text-right">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* House Rules */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          Reglas de la Casa
        </h3>
        <ul className="space-y-2 text-slate-300 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            Check-in: 3:00 PM / Check-out: 11:00 AM
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            No fumar dentro del apartamento
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            No fiestas ni eventos
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            Mascotas bajo consulta previa
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            Respetar horarios de silencio (10 PM - 8 AM)
          </li>
        </ul>
      </div>

      {/* Local Recommendations */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-400" />
          Recomendaciones Locales
        </h3>
        <div className="space-y-3">
          {recommendations.map((place, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div>
                <p className="text-white font-medium">{place.name}</p>
                <p className="text-slate-400 text-sm">{place.type} • {place.distance}</p>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="text-white">{place.rating}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* QR Modal */}
      {showQR && (
        <div 
          className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
        >
          <div className="bg-white p-8 rounded-2xl text-center max-w-sm">
            <h3 className="text-slate-900 font-bold text-xl mb-2">Guía del Huésped</h3>
            <p className="text-slate-500 text-sm mb-4">Escanea para acceder a la guía digital</p>
            <div className="w-48 h-48 bg-[var(--v-surface-1)] mx-auto rounded-lg flex items-center justify-center mb-4">
              <QrCode className="w-40 h-40 text-white" />
            </div>
            <p className="text-slate-400 text-xs">Tu propiedad</p>
            <p className="text-slate-900 font-mono mt-2">vecindus.com/guest/abc123</p>
          </div>
        </div>
      )}
    </div>
  );
}
